import {
  Boxes, Bus, Car, Package, PanelLeft, Truck
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type AssetType =
  | 'TRACTOR_HEAD'
  | 'TRUCK'
  | 'VAN'
  | 'PICKUP'
  | 'CAR'
  | 'TRAILER'
  | 'TANKER'
  | 'CONTAINER_CHASSIS'
  | 'HYDRAULIC_EQUIPMENT'

export type VehicleIconKey =
  | 'tractor-head'
  | 'truck'
  | 'van'
  | 'pickup'
  | 'car'
  | 'trailer'
  | 'tanker'
  | 'container-chassis'
  | 'hydraulic-equipment'
  | 'container'

export type TrackingStatus = 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'

export const ASSET_TYPE_OPTIONS: Array<{
  value: AssetType
  label: string
  iconKey: VehicleIconKey
}> = [
  { value: 'TRACTOR_HEAD', label: 'Tractor head', iconKey: 'tractor-head' },
  { value: 'TRUCK', label: 'Rigid truck', iconKey: 'truck' },
  { value: 'VAN', label: 'Van', iconKey: 'van' },
  { value: 'PICKUP', label: 'Pickup', iconKey: 'pickup' },
  { value: 'CAR', label: 'Car', iconKey: 'car' },
  { value: 'TANKER', label: 'Fuel tanker / cistern trailer', iconKey: 'tanker' },
  { value: 'CONTAINER_CHASSIS', label: 'Container chassis', iconKey: 'container-chassis' },
]

export const VEHICLE_ICON_OPTIONS: Array<{
  key: VehicleIconKey
  label: string
  Icon: LucideIcon
}> = [
  { key: 'tractor-head', label: 'Tractor head', Icon: Truck },
  { key: 'truck', label: 'Truck', Icon: Boxes },
  { key: 'van', label: 'Van', Icon: Bus },
  { key: 'pickup', label: 'Pickup', Icon: Truck },
  { key: 'car', label: 'Car', Icon: Car },
  { key: 'tanker', label: 'Fuel tanker trailer', Icon: Truck },
  { key: 'container-chassis', label: 'Container chassis', Icon: PanelLeft },
  { key: 'container', label: 'ISO container', Icon: Package }
]

const iconMap = new Map(VEHICLE_ICON_OPTIONS.map(option => [option.key, option.Icon]))
const typeIconMap = new Map(ASSET_TYPE_OPTIONS.map(option => [option.value, option.iconKey]))

export const defaultIconForType = (type?: string): VehicleIconKey =>
  typeIconMap.get(String(type || '').toUpperCase() as AssetType) || 'truck'

export const vehicleIconComponent = (iconKey?: string, assetType?: string, isContainer = false): LucideIcon => {
  const key = isContainer ? 'container' : (iconKey || defaultIconForType(assetType))
  return iconMap.get(key as VehicleIconKey) || Truck
}

/** Below this a fix is noise around a stationary vehicle, not movement. */
const MOVING_SPEED_KMH = 3

export interface TrackingPresentation {
  label: string
  /** i18n key for the same label; use t(labelKey, { defaultValue: label }). */
  labelKey: string
  color: string
  badge: string
  icon: string
}

/**
 * One colour language for the map, the list and the modals:
 * blue while the vehicle is driving, green while it is stopped but still
 * transmitting, red once the signal is gone, grey when nothing tracks it.
 *
 * Parked used to be orange; the customer reads orange as a warning, and a
 * vehicle that is parked and reporting is the healthy case (Nana, 12 Oct).
 */
export const trackingPresentation = (status?: string, currentSpeed?: number | null): TrackingPresentation => {
  const normalized = String(status || '').toUpperCase() as TrackingStatus
  if (normalized === 'LIVE') {
    if (currentSpeed != null && currentSpeed > MOVING_SPEED_KMH) {
      return {
        label: 'Moving',
        labelKey: 'tracking_moving',
        color: '#2563EB',
        badge: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: 'text-blue-600'
      }
    }
    return {
      label: 'Parked',
      labelKey: 'tracking_parked',
      color: '#16A34A',
      badge: 'bg-green-50 text-green-700 border-green-200',
      icon: 'text-green-600'
    }
  }
  // Stale and offline are the same fact to a dispatcher: the vehicle is no
  // longer reporting, so they share the red.
  if (normalized === 'STALE' || normalized === 'OFFLINE') {
    return {
      label: normalized === 'STALE' ? 'No signal' : 'Offline',
      labelKey: normalized === 'STALE' ? 'tracking_no_signal' : 'tracking_offline',
      color: '#DC2626',
      badge: 'bg-red-50 text-red-700 border-red-200',
      icon: 'text-red-600'
    }
  }
  return {
    label: 'Not tracked',
    labelKey: 'tracking_not_tracked',
    color: '#94A3B8',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: 'text-slate-500'
  }
}

// Stroke paths from the corresponding Lucide icons, used by DOM-based map markers.
export const mapIconSvg = (iconKey?: string, assetType?: string, isContainer = false) => {
  const key = isContainer ? 'container' : (iconKey || defaultIconForType(assetType))
  const paths: Record<string, string> = {
    container: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
    trailer: '<path d="M3 6h13v10H3z"/><path d="M16 10h3l2 3v3h-5"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    tanker: '<path d="M3 8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v7H3Z"/><path d="M7 4v11M15 4v11"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    'container-chassis': '<path d="M3 14h16"/><path d="M5 9v5M17 9v5"/><path d="M2 9h18"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    'hydraulic-equipment': '<path d="M14.7 6.3a4 4 0 0 0-5-5L7 4l3 3-3 3-3-3-2.7 2.7a4 4 0 0 0 5 5L14 22l8-8Z"/>',
    van: '<path d="M3 7h12v10H3z"/><path d="M15 10h4l2 4v3h-6"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    car: '<path d="m5 11 2-5h10l2 5"/><path d="M3 11h18v6H3z"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>',
    pickup: '<path d="M3 7h9v9H3z"/><path d="M12 11h8l1 5h-9"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    truck: '<path d="M14 18V6H2v11h3"/><path d="M15 18H9"/><path d="M19 18h2v-5l-3-4h-4"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
    'tractor-head': '<path d="M13 18V7H5v10h2"/><path d="M14 18h-3"/><path d="M17 18h4v-6l-3-4h-5"/><circle cx="9" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>'
  }
  return paths[key] || paths.truck
}
